/**
 * useVersionCheck Hook
 *
 * Periodically checks for application updates by comparing version information
 * with the server. Surfaces two independent signals:
 *
 *   1. `updateAvailable` / `newVersion` — the existing "FYI a newer build exists"
 *      banner state, dismissible per-session.
 *   2. `securityState` / `securityManifest` — the gematik-mandated A_25549..A_25551
 *      security-update flow (informational reminder → escalation → forced lock),
 *      derived independently of (1) so the lock cannot accidentally be gated on
 *      "is there a newer build than the running one".
 *
 * Anti-bypass design:
 *   - The most recent successful manifest's threshold is persisted to
 *     localStorage (sticky threshold); the gate is seeded synchronously on boot
 *     before any fetch resolves.
 *   - A monotonic-clock guard records the highest Date.now() ever observed;
 *     lock decisions use max(Date.now(), storedMaxNow), neutralising attempts
 *     to escape by setting the clock backwards.
 *   - On a fresh app start with NO sticky threshold AND the first fetch hasn't
 *     resolved within 30 s, the gate fails open to 'none'. Any subsequent
 *     successful fetch with a threshold that locks the user takes effect.
 */

import { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import packageJson from '../../package.json';
import { isBelowMinVersion } from '../utils/semver';

const CURRENT_VERSION = packageJson.version;

// Poll meta.json every 5 minutes.
const CHECK_INTERVAL = 5 * 60 * 1000;

const FETCH_TIMEOUT = 10000; // 10 seconds
const DISMISSAL_KEY = 'app-version-update-dismissed';
const RELOAD_ATTEMPT_KEY = 'app-version-reload-attempted';
const RELOAD_ATTEMPT_TIMEOUT = 10 * 60 * 1000; // 10 minutes - prevent reload loops

// Per-session dismissal of the non-security banner lasts 1 hour, then re-prompts.
const DISMISSAL_TIMEOUT = 60 * 60 * 1000;

// Security update gate state — localStorage keys
const STICKY_THRESHOLD_KEY = 'ti_security_sticky_threshold';
const MAX_NOW_KEY = 'ti_security_max_now';

// Cold-start splash budget: how long to block on the very first fetch when
// there is no sticky threshold to seed from.
const COLD_START_SPLASH_MS = 30 * 1000;

// Debounce window for visibilitychange-triggered re-checks.
const VISIBILITY_RECHECK_DEBOUNCE_MS = 30 * 1000;

// Conditional logging based on environment
const logger = {
  log: process.env.NODE_ENV === 'development' ? console.log.bind(console) : () => {},
  warn: console.warn.bind(console),
  error: console.error.bind(console),
};

/**
 * Soft-validate a single optional manifest field. Returns the value if
 * it passes type/format checks, else undefined. A malformed field must
 * be treated as absent — never reject the whole manifest because of it.
 */
const sanitiseMinVersion = (v) =>
  typeof v === 'string' && v.trim().length > 0 ? v.trim() : undefined;

const sanitiseLockAt = (v) => {
  if (typeof v !== 'string') return undefined;
  const ms = Date.parse(v);
  return Number.isFinite(ms) ? v : undefined;
};

const sanitiseSecurityNotice = (v) =>
  v === 'critical' || v === 'recommended' ? v : undefined;

const sanitiseSupportContact = (v) =>
  typeof v === 'string' && v.trim().length > 0 ? v.trim() : undefined;

const readStickyThreshold = () => {
  try {
    const raw = localStorage.getItem(STICKY_THRESHOLD_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    return {
      minVersion: sanitiseMinVersion(parsed.minVersion),
      lockAt: sanitiseLockAt(parsed.lockAt),
      securityNotice: sanitiseSecurityNotice(parsed.securityNotice),
      supportContact: sanitiseSupportContact(parsed.supportContact),
    };
  } catch (err) {
    logger.warn('[VersionCheck] Could not read sticky threshold:', err);
    return null;
  }
};

const writeStickyThreshold = (manifest) => {
  try {
    localStorage.setItem(STICKY_THRESHOLD_KEY, JSON.stringify({
      minVersion: manifest.minVersion,
      lockAt: manifest.lockAt,
      securityNotice: manifest.securityNotice,
      supportContact: manifest.supportContact,
      capturedAt: new Date().toISOString(),
    }));
  } catch (err) {
    logger.warn('[VersionCheck] Could not persist sticky threshold:', err);
  }
};

const clearStickyThreshold = () => {
  try {
    localStorage.removeItem(STICKY_THRESHOLD_KEY);
  } catch (err) {
    logger.warn('[VersionCheck] Could not clear sticky threshold:', err);
  }
};

/**
 * Monotonic clock guard. Returns max(Date.now(), stored), and advances the
 * stored value if Date.now() is ahead. Called on every securityState
 * evaluation (not only on poll) so a clock-rewind attack cannot exploit
 * the 5-min window between polls.
 */
const advanceMaxNow = () => {
  const real = Date.now();
  try {
    const raw = localStorage.getItem(MAX_NOW_KEY);
    const stored = raw ? parseInt(raw, 10) : 0;
    const effective = Math.max(real, Number.isFinite(stored) ? stored : 0);
    if (effective > stored) {
      localStorage.setItem(MAX_NOW_KEY, String(effective));
    }
    return effective;
  } catch (err) {
    logger.warn('[VersionCheck] Monotonic clock guard failed; using Date.now():', err);
    return real;
  }
};

/**
 * Classify the security gate state from current version + threshold fields.
 * Pure function — fully deterministic given inputs. Does NOT depend on
 * updateAvailable / newVersion (kept separate by design).
 *
 *   'none'   — no threshold, or current is at/above minVersion
 *   'soft'   — current < minVersion, lockAt is unknown OR > 24h away
 *   'hard'   — current < minVersion, ≤24h until lockAt
 *   'locked' — current < minVersion, effectiveNow >= lockAt
 */
const classifySecurityState = ({ currentVersion, minVersion, lockAt, effectiveNow }) => {
  if (!minVersion || !isBelowMinVersion(currentVersion, minVersion)) {
    return 'none';
  }
  const lockMs = lockAt ? Date.parse(lockAt) : NaN;
  if (!Number.isFinite(lockMs)) {
    // Below minimum but no valid deadline — keep the soft reminder going.
    return 'soft';
  }
  if (effectiveNow >= lockMs) return 'locked';
  if (lockMs - effectiveNow <= 24 * 60 * 60 * 1000) return 'hard';
  return 'soft';
};

export const useVersionCheck = () => {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [newVersion, setNewVersion] = useState(null);
  const [newBuildTime, setNewBuildTime] = useState(null);

  // Security gate inputs. Seed synchronously from sticky threshold so the
  // lock is up before the first fetch resolves.
  const [securityManifest, setSecurityManifest] = useState(() => readStickyThreshold() || {});
  const [firstFetchResolved, setFirstFetchResolved] = useState(false);
  const [splashExpired, setSplashExpired] = useState(false);
  // `tick` is bumped on each successful poll / visibility re-check so the
  // securityState useMemo re-runs and re-evaluates the monotonic clock guard.
  const [tick, setTick] = useState(0);

  const checkTimeoutRef = useRef(null);
  const abortControllerRef = useRef(null);
  const shouldCheckRef = useRef(true);
  const lastCheckedAtRef = useRef(0);
  const stickySeededRef = useRef(readStickyThreshold() !== null);

  /**
   * Parse and validate dismissed version from sessionStorage
   * Checks if dismissal has expired based on DISMISSAL_TIMEOUT
   * @returns {string|null} The dismissed version or null if expired/invalid
   */
  const getDismissedVersion = () => {
    try {
      const dismissed = sessionStorage.getItem(DISMISSAL_KEY);
      if (!dismissed) return null;

      const parsed = JSON.parse(dismissed);

      // Validate schema
      if (!parsed || typeof parsed.version !== 'string' || !parsed.dismissedAt) {
        logger.warn('[VersionCheck] Invalid dismissal data, removing');
        sessionStorage.removeItem(DISMISSAL_KEY);
        return null;
      }

      // Check if dismissal has expired
      const dismissedTime = new Date(parsed.dismissedAt).getTime();
      const currentTime = Date.now();
      const timeSinceDismissal = currentTime - dismissedTime;

      // Handle invalid timestamps (NaN) or future timestamps (clock changes)
      if (isNaN(dismissedTime) || timeSinceDismissal < 0) {
        logger.warn('[VersionCheck] Invalid or future timestamp detected, clearing dismissal');
        sessionStorage.removeItem(DISMISSAL_KEY);
        return null;
      }

      if (timeSinceDismissal >= DISMISSAL_TIMEOUT) {
        logger.log('[VersionCheck] Dismissal EXPIRED after', Math.round(timeSinceDismissal / 60000), 'min - clearing');
        sessionStorage.removeItem(DISMISSAL_KEY);
        return null;
      }

      logger.log('[VersionCheck] Dismissal ACTIVE -', Math.round((DISMISSAL_TIMEOUT - timeSinceDismissal) / 60000), 'min remaining');
      return parsed.version;
    } catch (error) {
      logger.error('[VersionCheck] Failed to parse dismissal data:', error);
      sessionStorage.removeItem(DISMISSAL_KEY);
      return null;
    }
  };

  /**
   * Store dismissed version in sessionStorage
   * @param {string} version - The version to mark as dismissed
   */
  const setDismissedVersion = (version) => {
    try {
      sessionStorage.setItem(DISMISSAL_KEY, JSON.stringify({
        version,
        dismissedAt: new Date().toISOString(),
      }));
    } catch (error) {
      logger.error('[VersionCheck] Failed to save dismissal:', error);
    }
  };

  /**
   * Validate meta.json response schema
   * @param {any} data - The parsed JSON response
   * @returns {boolean} True if valid, false otherwise
   */
  const isValidMetaResponse = (data) => {
    return (
      data &&
      typeof data === 'object' &&
      typeof data.version === 'string' &&
      data.version.trim().length > 0
    );
  };

  /**
   * Check for new version by fetching meta.json
   * Uses cache-busting and timeout to ensure fresh data
   */
  const checkVersion = async () => {
    // Abort any existing request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create new abort controller for this request
    abortControllerRef.current = new AbortController();
    const timeout = setTimeout(() => {
      abortControllerRef.current.abort();
    }, FETCH_TIMEOUT);

    try {
      // Add timestamp to prevent caching
      const timestamp = Date.now();
      const response = await fetch(`/meta.json?t=${timestamp}`, {
        cache: 'no-store',
        signal: abortControllerRef.current.signal,
        headers: {
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache',
        },
      });

      clearTimeout(timeout);

      if (!response.ok) {
        logger.warn('[VersionCheck] Failed to fetch version info:', response.status);
        return;
      }

      const data = await response.json();

      // Validate response schema
      if (!isValidMetaResponse(data)) {
        logger.warn('[VersionCheck] Invalid meta.json response:', data);
        return;
      }

      // Soft-validate optional security fields (malformed fields are dropped,
      // never reject the whole manifest)
      const sanitised = {
        minVersion: sanitiseMinVersion(data.minVersion),
        lockAt: sanitiseLockAt(data.lockAt),
        securityNotice: sanitiseSecurityNotice(data.securityNotice),
        supportContact: sanitiseSupportContact(data.supportContact),
      };

      // Sticky threshold persistence:
      //   - If manifest says current ≥ minVersion (or no minVersion), CLEAR sticky.
      //   - Else PERSIST the threshold so reload/offline can't escape the lock.
      const stillBelowMin =
        sanitised.minVersion && isBelowMinVersion(CURRENT_VERSION, sanitised.minVersion);
      if (stillBelowMin) {
        writeStickyThreshold(sanitised);
      } else {
        clearStickyThreshold();
      }
      setSecurityManifest(sanitised);
      setFirstFetchResolved(true);
      lastCheckedAtRef.current = Date.now();
      setTick((t) => t + 1);

      const serverVersion = data.version;
      const serverBuildTime = data.buildTime; // Optional field

      // Check if user has dismissed this update in current session
      const dismissedVersion = getDismissedVersion();

      logger.log('[VersionCheck] Check:', CURRENT_VERSION, 'vs', serverVersion, '| Dismissed:', dismissedVersion || 'none');

      // Show update notification if:
      // 1. Server version is different from current version
      // 2. User hasn't dismissed this specific version (or dismissal expired)
      if (serverVersion !== CURRENT_VERSION && serverVersion !== dismissedVersion) {
        logger.log('[VersionCheck] SHOW modal - version mismatch, not dismissed');
        setNewVersion(serverVersion);
        setNewBuildTime(serverBuildTime);
        setUpdateAvailable(true);

        // Keep polling to detect when dismissal expires
        logger.log('[VersionCheck] Update shown, continuing checks for dismissal expiration');
      } else if (serverVersion !== CURRENT_VERSION && serverVersion === dismissedVersion) {
        // Update is available but dismissed - need to hide notification if showing
        logger.log('[VersionCheck] HIDE modal - user dismissed this version');
        setUpdateAvailable(false);
      } else {
        logger.log('[VersionCheck] NO ACTION - already on latest version');
        setUpdateAvailable(false);
      }
    } catch (error) {
      clearTimeout(timeout);

      // Don't log abort errors (they're expected when component unmounts)
      if (error.name === 'AbortError') {
        logger.log('[VersionCheck] Request aborted');
      } else {
        // Silently fail to avoid disrupting user experience
        logger.error('[VersionCheck] Error checking version:', error);
      }
    }
  };

  /**
   * Check if we've recently attempted a reload to prevent infinite loops
   * @returns {boolean} True if reload was recently attempted
   */
  const hasRecentReloadAttempt = () => {
    try {
      const attemptData = sessionStorage.getItem(RELOAD_ATTEMPT_KEY);
      if (!attemptData) return false;

      const { timestamp, version } = JSON.parse(attemptData);
      const timeSinceAttempt = Date.now() - timestamp;

      // If we attempted reload for this version within the timeout window, prevent retry
      if (version === newVersion && timeSinceAttempt < RELOAD_ATTEMPT_TIMEOUT) {
        logger.warn('[VersionCheck] Recent reload attempt detected, preventing loop');
        return true;
      }

      // Cleanup old attempt data
      sessionStorage.removeItem(RELOAD_ATTEMPT_KEY);
      return false;
    } catch (error) {
      logger.error('[VersionCheck] Error checking reload attempt:', error);
      return false;
    }
  };

  /**
   * Apply the update by clearing caches and reloading the page
   * Includes protection against infinite reload loops
   */
  const applyUpdate = useCallback(async () => {
    // Check if we've already tried to reload recently
    if (hasRecentReloadAttempt()) {
      logger.error('[VersionCheck] Already attempted reload recently - possible cache issue');

      // Hide the modal to prevent stuck state
      setUpdateAvailable(false);

      // Show user-friendly instructions
      alert(
        'Unable to update automatically. Please:\n' +
        '1. Press Ctrl+Shift+R (Windows/Linux) or Cmd+Shift+R (Mac) to hard refresh\n' +
        '2. Or clear your browser cache manually\n' +
        '3. Then reload the page',
      );
      return;
    }

    logger.log('[VersionCheck] Applying update - clearing caches and reloading');

    // Track this reload attempt to prevent loops
    try {
      sessionStorage.setItem(RELOAD_ATTEMPT_KEY, JSON.stringify({
        timestamp: Date.now(),
        version: newVersion,
      }));
    } catch (error) {
      logger.error('[VersionCheck] Error saving reload attempt:', error);
    }

    // Clear service worker caches if available
    if ('caches' in window) {
      try {
        const names = await caches.keys();
        await Promise.all(names.map(name => {
          logger.log('[VersionCheck] Clearing cache:', name);
          return caches.delete(name);
        }));
      } catch (err) {
        logger.error('[VersionCheck] Error clearing caches:', err);
      }
    }

    // Clear session storage dismissal
    try {
      sessionStorage.removeItem(DISMISSAL_KEY);
    } catch (error) {
      logger.error('[VersionCheck] Error clearing session storage:', error);
    }

    // Force hard refresh by adding cache-busting parameter
    // Note: reload(true) is deprecated, so we use this approach instead
    const url = new URL(window.location.href);
    url.searchParams.set('_refresh', Date.now());
    window.location.href = url.toString();
  }, [newVersion]);

  /**
   * Dismiss the update notification temporarily (for DISMISSAL_TIMEOUT duration)
   */
  const dismissUpdate = useCallback(() => {
    logger.log('[VersionCheck] User dismissed update notification for version:', newVersion);

    // Store dismissed version in session storage
    setDismissedVersion(newVersion);

    setUpdateAvailable(false);
  }, [newVersion]);

  /**
   * Setup periodic version checking
   */
  useEffect(() => {
    logger.log('[VersionCheck] Initializing version check system');
    logger.log('[VersionCheck] Check interval:', CHECK_INTERVAL / 1000, 'seconds');

    // Check immediately on mount
    checkVersion();

    // Set up periodic checking
    const scheduleNextCheck = () => {
      checkTimeoutRef.current = setTimeout(() => {
        checkVersion();
        // Only schedule next check if we should continue (uses ref to avoid stale closure)
        if (shouldCheckRef.current) {
          scheduleNextCheck();
        }
      }, CHECK_INTERVAL);
    };

    scheduleNextCheck();

    // Re-check on tab focus, debounced — a tab left open overnight should lock
    // without manual reload.
    const handleVisibility = () => {
      if (document.visibilityState !== 'visible') return;
      const sinceLast = Date.now() - lastCheckedAtRef.current;
      if (sinceLast > VISIBILITY_RECHECK_DEBOUNCE_MS) {
        logger.log('[VersionCheck] visibilitychange → re-check (debounce ok)');
        checkVersion();
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);

    // Cold-start splash: if we have no sticky threshold AND the first fetch
    // hasn't resolved within COLD_START_SPLASH_MS, fail open. A subsequent
    // successful fetch with a threshold that locks still takes effect.
    let splashTimer = null;
    if (!stickySeededRef.current) {
      splashTimer = setTimeout(() => {
        setSplashExpired(true);
      }, COLD_START_SPLASH_MS);
    } else {
      setSplashExpired(true);
    }

    // Cleanup on unmount
    return () => {
      shouldCheckRef.current = false; // Stop any pending checks
      if (checkTimeoutRef.current) {
        clearTimeout(checkTimeoutRef.current);
        logger.log('[VersionCheck] Cleanup: cleared check timeout');
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        logger.log('[VersionCheck] Cleanup: aborted pending request');
      }
      document.removeEventListener('visibilitychange', handleVisibility);
      if (splashTimer) clearTimeout(splashTimer);
    };
  }, []); // Empty dependency array - only run once on mount

  // Derive securityState. Recomputes whenever inputs change AND on every
  // successful fetch (via `tick`). Each evaluation advances the monotonic
  // clock guard so a clock-rewind attack cannot exploit the gap between polls.
  // Intentionally does NOT depend on updateAvailable/newVersion — the lock
  // must not be gated on whether a newer build than the running one exists.
  const securityState = useMemo(() => {
    const effectiveNow = advanceMaxNow();
    const sticky = stickySeededRef.current ? readStickyThreshold() : null;
    // Prefer fresh fetch values; fall back to sticky only for fields that
    // are still missing. This handles legitimate rollbacks: when the fresh
    // fetch returns no minVersion, sticky has already been cleared above.
    const minVersion = securityManifest.minVersion || sticky?.minVersion;
    const lockAt = securityManifest.lockAt || sticky?.lockAt;

    if (!firstFetchResolved && !stickySeededRef.current && !splashExpired) {
      return 'verifying';
    }

    return classifySecurityState({
      currentVersion: CURRENT_VERSION,
      minVersion,
      lockAt,
      effectiveNow,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [securityManifest, firstFetchResolved, splashExpired, tick]);

  // Effective security manifest exposed to the UI: merge fresh + sticky so
  // technical details on the lock screen are populated even after a fetch
  // failure.
  const effectiveSecurityManifest = useMemo(() => {
    const sticky = stickySeededRef.current ? readStickyThreshold() : null;
    return {
      minVersion: securityManifest.minVersion || sticky?.minVersion,
      lockAt: securityManifest.lockAt || sticky?.lockAt,
      securityNotice: securityManifest.securityNotice || sticky?.securityNotice,
      supportContact: securityManifest.supportContact || sticky?.supportContact,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [securityManifest, tick]);

  return {
    updateAvailable,
    newVersion,
    newBuildTime,
    currentVersion: CURRENT_VERSION,
    applyUpdate,
    dismissUpdate,
    securityState,
    securityManifest: effectiveSecurityManifest,
  };
};
